import { useState } from 'react'
import { Phone, Settings, Save, X, Copy, Check, AlertCircle, Zap } from 'lucide-react'

export default function TextNowAIPhoneConfigPage() {
  const [activeTab, setActiveTab] = useState('setup')
  const [copied, setCopied] = useState(false)
  const [phoneConfig, setPhoneConfig] = useState({
    textnowApiKey: '',
    textnowPhoneNumber: '410-701-2931',
    aiAssistantEnabled: true,
    autoAnswerEnabled: true,
    recordCalls: true,
    sentimentAnalysis: true,
    transcriptionEnabled: true,
    voiceSpeed: 1.0,
    responseTimeout: 30,
    departments: {
      support: { enabled: true, transferTo: 'support@example.com' },
      sales: { enabled: true, transferTo: 'sales@example.com' },
      billing: { enabled: true, transferTo: 'billing@example.com' },
      general: { enabled: true, transferTo: 'general@example.com' },
    },
  })

  const [webhookUrl, setWebhookUrl] = useState(
    'https://cybertection.guardbot.local/api/webhooks/textnow-calls'
  )

  const handlePhoneConfigChange = (field, value) => {
    setPhoneConfig({ ...phoneConfig, [field]: value })
  }

  const handleDepartmentChange = (dept, field, value) => {
    setPhoneConfig({
      ...phoneConfig,
      departments: {
        ...phoneConfig.departments,
        [dept]: {
          ...phoneConfig.departments[dept],
          [field]: value,
        },
      },
    })
  }

  const handleSaveConfig = () => {
    localStorage.setItem('textnow-phone-config', JSON.stringify(phoneConfig))
    alert('Phone configuration saved successfully!')
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(webhookUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-4 bg-blue-600/20 rounded-lg">
            <Phone size={32} className="text-blue-400" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-white">TextNow AI Phone Assistant</h1>
            <p className="text-slate-400">Configure AI-powered phone answering system</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-blue-500/20 pb-4">
        {[
          { id: 'setup', label: 'Setup', icon: Settings },
          { id: 'config', label: 'Configuration', icon: Zap },
          { id: 'departments', label: 'Departments', icon: Phone },
          { id: 'webhook', label: 'Webhook', icon: AlertCircle },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-600/40 to-cyan-600/40 text-blue-200 border border-blue-500/50'
                : 'text-slate-400 hover:text-blue-300'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Setup Tab */}
      {activeTab === 'setup' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-8 rounded-lg border border-blue-500/20">
            <h2 className="text-2xl font-bold text-white mb-6">Getting Started</h2>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="bg-slate-800/50 p-6 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-green-600/30 flex items-center justify-center text-green-300 font-bold">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-white">Get TextNow API Key</h3>
                </div>
                <p className="text-slate-400 mb-4">
                  Go to <span className="text-blue-400 font-mono">app.textnow.com/settings/api</span> and create an API
                  key
                </p>
                <div className="bg-slate-900/50 p-4 rounded border border-slate-700">
                  <label className="block text-sm text-slate-400 mb-2">TextNow API Key</label>
                  <input
                    type="password"
                    placeholder="sk_live_... or sk_test_..."
                    value={phoneConfig.textnowApiKey}
                    onChange={e => handlePhoneConfigChange('textnowApiKey', e.target.value)}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-slate-800/50 p-6 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-green-600/30 flex items-center justify-center text-green-300 font-bold">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-white">Configure Your Phone Number</h3>
                </div>
                <p className="text-slate-400 mb-4">Enter your TextNow phone number for incoming calls</p>
                <div className="bg-slate-900/50 p-4 rounded border border-slate-700">
                  <label className="block text-sm text-slate-400 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    placeholder="+1-417-701-2931"
                    value={phoneConfig.textnowPhoneNumber}
                    onChange={e => handlePhoneConfigChange('textnowPhoneNumber', e.target.value)}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-slate-800/50 p-6 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-green-600/30 flex items-center justify-center text-green-300 font-bold">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-white">Set Webhook URL</h3>
                </div>
                <p className="text-slate-400 mb-4">Configure TextNow to send call events to this webhook</p>
                <div className="bg-slate-900/50 p-4 rounded border border-slate-700">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={webhookUrl}
                      readOnly
                      className="flex-1 px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-4 py-2 bg-blue-600/20 text-blue-300 rounded hover:bg-blue-600/30 transition-all"
                    >
                      {copied ? <Check size={20} /> : <Copy size={20} />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Enter this URL in TextNow Settings → Webhooks → Call Events
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-slate-800/50 p-6 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-green-600/30 flex items-center justify-center text-green-300 font-bold">
                    4
                  </div>
                  <h3 className="text-xl font-bold text-white">Enable Features</h3>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={phoneConfig.aiAssistantEnabled}
                      onChange={e => handlePhoneConfigChange('aiAssistantEnabled', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-white">Enable AI Assistant</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={phoneConfig.autoAnswerEnabled}
                      onChange={e => handlePhoneConfigChange('autoAnswerEnabled', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-white">Auto Answer Calls</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={phoneConfig.recordCalls}
                      onChange={e => handlePhoneConfigChange('recordCalls', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-white">Record Calls</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={phoneConfig.transcriptionEnabled}
                      onChange={e => handlePhoneConfigChange('transcriptionEnabled', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-white">Enable Transcription</span>
                  </label>
                </div>
              </div>

              <button
                onClick={handleSaveConfig}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all"
              >
                <Save size={20} className="inline mr-2" />
                Save & Activate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 rounded-lg border border-blue-500/20">
            <h2 className="text-2xl font-bold text-white mb-6">AI Assistant Configuration</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Voice Speed (0.5 - 2.0)</label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={phoneConfig.voiceSpeed}
                  onChange={e => handlePhoneConfigChange('voiceSpeed', parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-slate-500 mt-1">Current: {phoneConfig.voiceSpeed}x</p>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Response Timeout (seconds)</label>
                <input
                  type="number"
                  min="5"
                  max="120"
                  value={phoneConfig.responseTimeout}
                  onChange={e => handlePhoneConfigChange('responseTimeout', parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-500/50"
                />
              </div>

              <div className="pt-4">
                <h3 className="text-lg font-bold text-white mb-3">AI Behavior</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={phoneConfig['sentiment Analysis']}
                      onChange={e => handlePhoneConfigChange('sentiment Analysis', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-white">Analyze Call Sentiment</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                    <span className="text-white">Escalate to Human on Frustration</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                    <span className="text-white">Learn from Call Interactions</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Departments Tab */}
      {activeTab === 'departments' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 rounded-lg border border-blue-500/20">
            <h2 className="text-2xl font-bold text-white mb-6">Intelligent Call Routing</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(phoneConfig.departments).map(([dept, config]) => (
                <div key={dept} className="bg-slate-800/50 p-4 rounded border border-slate-700">
                  <div className="flex items-center gap-2 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={e => handleDepartmentChange(dept, 'enabled', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-white font-semibold capitalize">{dept}</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Transfer to</label>
                    <input
                      type="email"
                      value={config.transferTo}
                      onChange={e => handleDepartmentChange(dept, 'transferTo', e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-blue-500/20 rounded text-sm text-white focus:outline-none focus:border-blue-500/50"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleSaveConfig}
              className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all"
            >
              <Save size={20} className="inline mr-2" />
              Save Configuration
            </button>
          </div>
        </div>
      )}

      {/* Webhook Tab */}
      {activeTab === 'webhook' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 rounded-lg border border-blue-500/20">
            <h2 className="text-2xl font-bold text-white mb-6">Webhook Configuration</h2>

            <div className="space-y-6">
              <div className="bg-slate-800/50 p-6 rounded border border-cyan-500/20">
                <h3 className="text-lg font-bold text-white mb-4">Webhook URL</h3>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={webhookUrl}
                    readOnly
                    className="flex-1 px-4 py-3 bg-slate-700/50 border border-blue-500/20 rounded text-white font-mono text-sm focus:outline-none"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-3 bg-blue-600/20 text-blue-300 rounded hover:bg-blue-600/30 transition-all"
                  >
                    {copied ? <Check size={20} /> : <Copy size={20} />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  ✓ Copy this URL and paste it into TextNow webhook settings
                </p>
              </div>

              <div className="bg-slate-800/50 p-6 rounded border border-yellow-500/20">
                <h3 className="text-lg font-bold text-white mb-4">Webhook Events</h3>
                <ul className="space-y-3 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-green-400 mt-0.5" />
                    <span><span className="text-white font-mono">call.inbound.started</span> - New incoming call</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-green-400 mt-0.5" />
                    <span><span className="text-white font-mono">call.completed</span> - Call ended</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-green-400 mt-0.5" />
                    <span><span className="text-white font-mono">transcription.ready</span> - Call transcribed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-green-400 mt-0.5" />
                    <span><span className="text-white font-mono">recording.ready</span> - Recording available</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-800/50 p-6 rounded border border-purple-500/20">
                <h3 className="text-lg font-bold text-white mb-4">Example Payload</h3>
                <pre className="text-xs text-slate-300 bg-slate-900/50 p-4 rounded overflow-auto max-h-64">
{`{
  "event": "call.inbound.started",
  "callId": "call_123456",
  "from": "+1-555-123-4567",
  "to": "+1-410-701-2931",
  "timestamp": "2026-04-28T14:30:00Z",
  "duration": 0,
  "recordingUrl": null
}`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
